import styles from "./StaffPopup.module.scss";
import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { Form, FormInstance, message } from "antd";
import FormItemInput from "../../../components/ui/FormItemInput/FormItemInput";
import FormItemInputPassword from "../../../components/ui/FormItemInputPassword/FormItemInputPassword";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import { ICreateStaffModel, IStaffItem, IUpdateStaffModel } from "../../../models/Staff";
import { emailValidation, passwordValidation } from "../../../helper/auth";
import useAppSelector from "../../../hooks/useAppSelector";
import FormItemSelect from "../../../components/ui/FormItemSelect/FormItemSelect";
import { useNavigate } from "react-router-dom";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { openErrorMessage, openSuccessMessage } from "../../../store/message/messageSlice";
import { handleError } from "../../../helper/errorHandler";
import { useStaffMutations } from "../../../api/mutations";

interface StaffPopupProps {
  activeTab: "admin" | "support";
  staffItem: null | IStaffItem;
  setShowCreatePopup: (value: { show: boolean; activeTab: "admin" | "support" }) => void;
}

const StaffPopup: React.FC<StaffPopupProps> = ({ activeTab, staffItem, setShowCreatePopup }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { allCountries } = useAppSelector((state) => state.countriesSlice);
  const { t } = useTranslation();
  const [countries, setCountries] = useState<{ id: string; label: string }[]>([]);
  const formRef = useRef<FormInstance>(null);

  const { createAdmin, updateAdmin, createSupport, updateSupport } = useStaffMutations();

  useEffect(() => {
    if (formRef.current && staffItem) {
      formRef.current.setFieldsValue({
        firstname: staffItem.firstname,
        lastname: staffItem.lastname,
        email: staffItem.email,
        phoneNumber: staffItem.contactInformation.phoneNumber,
        phoneCountryCode: staffItem.contactInformation.phoneCountryCode,
      });
    }
  }, [formRef.current, staffItem]);

  useEffect(() => {
    if (allCountries.length > 0) {
      const changedCountries = allCountries
        .map((el) => {
          return {
            id:
              (el.idd.root ? el.idd.root : "+999") +
              (el.idd.suffixes.length ? el.idd.suffixes[0] : ""),
            label:
              (el.idd.root ? el.idd.root : "+999") +
              (el.idd.suffixes.length ? el.idd.suffixes[0] : "") +
              " " +
              el.name.common,
          };
        })
        .sort((a, b) =>
          a.label.toLowerCase() < b.label.toLowerCase()
            ? -1
            : a.label.toLowerCase() > b.label.toLowerCase()
              ? 1
              : 0
        );
      setCountries(changedCountries);
    }
  }, [allCountries]);

  const onFinish = async (values: ICreateStaffModel) => {
    if (!emailValidation(values.email)) {
      dispatch(openErrorMessage(t("PleaseEnterEmailCorrectly")));
      return;
    }

    if (!passwordValidation(values.password) && !staffItem) {
      dispatch(openErrorMessage(t("ValidationPassword")));
      return;
    }

    const model = {
      email: values.email.trim(),
      firstname: values.firstname.trim(),
      lastname: values.lastname.trim(),
      phoneNumber: values.phoneNumber.trim(),
      phoneCountryCode: values.phoneCountryCode,
    };

    const withPassword = { ...model, password: values.password?.trim() };

    const commonOptions = {
      onSuccess: () => {
        dispatch(
          openSuccessMessage(
            t(staffItem ? "ProfileUpdatedSuccessfully" : "ProfileCreatedSuccessfully")
          )
        );
        redirect(true, activeTab);
      },
      onError: (e: any) => handleError(e, dispatch, t),
    };

    if (staffItem) {
      activeTab === "admin"
        ? updateAdmin({ id: staffItem.id, model }, commonOptions)
        : updateSupport({ id: staffItem.id, model }, commonOptions);
    } else {
      activeTab === "admin"
        ? createAdmin(withPassword, commonOptions)
        : createSupport(withPassword, commonOptions);
    }
  };

  const redirect = (shouldUpdate: boolean, activeTab: "admin" | "support") => {
    clickOnClose();
    navigate(`/staff/${activeTab}s/?shouldUpdate=${shouldUpdate}`);
  };

  const clickOnClose = () => {
    setShowCreatePopup({ show: false, activeTab });
  };

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        {staffItem ? (
          <h3 className={styles.title}>
            {activeTab === "admin" ? t("UpdateAdmin") : t("UpdateSupport")}
          </h3>
        ) : (
          <h3 className={styles.title}>
            {activeTab === "admin" ? t("AddNewAdmin") : t("AddNewSupport")}
          </h3>
        )}
        <Form
          className={styles.form}
          ref={formRef}
          name="create-staff"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <div className={styles.row}>
            <FormItemInput
              name={"firstname"}
              label={t("FirstName") + " *"}
              rules={{ required: true }}
            />
            <FormItemInput
              name={"lastname"}
              label={t("LastName") + " *"}
              rules={{ required: true }}
            />
          </div>

          <div className={styles.row}>
            <FormItemInput name={"email"} label={t("Email") + " *"} rules={{ required: true }} />
            {!staffItem && (
              <FormItemInputPassword
                autoComplete={"new-password"}
                name={"password"}
                label={t("Password") + " *"}
                rules={{ required: true }}
              />
            )}
          </div>

          <div className={styles.row}>
            <FormItemSelect
              showSearch={true}
              name={"phoneCountryCode"}
              label={t("CountryCode") + " *"}
              rules={{ required: true }}
              items={countries}
            />
            <FormItemInput
              onlyNumber={true}
              name={"phoneNumber"}
              label={t("PhoneNumber") + " *"}
              rules={{ required: true }}
            />
          </div>

          <div className={styles.btnsWrapper}>
            <CustomButton
              text={t("Close")}
              type={"button"}
              typeStyle={"outline"}
              functionOnClick={clickOnClose}
            />
            <CustomButton text={t("Save")} type={"submit"} typeStyle={"fill"} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default StaffPopup;
